import { useCombinedRefs } from '@visiba/react-std';
import { ForwardedRef, forwardRef, MouseEvent, useRef } from 'react';
import styled from 'styled-components';
import { Icon } from '../../icon';
import { Description } from '../base/description.component';
import { HelpText } from '../base/help_text.component';
import { InputProps, elements as inputElements } from '../base/input.component';
import { Label } from '../base/label.component';
import { theme as commonTheme } from '../common_input.theme';
import { useInput } from '../use_input.hook';
import { theme } from './input_date.theme';

export interface InputDateRootProps extends InputProps {
	/**
	 * The label of the input field.
	 */
	label: string;

	name?: string;

	/**
	 * Use to visually hide the label.
	 */
	hideLabel?: boolean;

	/**
	 * Use to add a descriptive text below the input field.
	 */
	helperText?: string;

	/**
	 * It's mandatory if `invalid` is set to `true` and only visible in that case
	 * too. The purpose with this is to provide a explanation why the input field is
	 * `invalid`. This text will replace the `helperText`, right below the text
	 * area.
	 */
	invalidText?: string;
	defaultValue?: string;

	type?: never;
	description?: string;
	optional?: boolean;
	onClick?: (event: MouseEvent<HTMLInputElement>) => void;
}

const elements = {
	container: styled.div`
		display: flex;
		flex-direction: column;
		flex: 1 1 auto;
	`,
	label: styled(Label)`
		margin-bottom: ${commonTheme.getValue('labelMarginBottom')};
	`,
	description: styled(Description)`
		margin-bottom: ${commonTheme.getValue('descriptionMarginBottom')};
	`,
	input: styled(inputElements.input)`
		padding-left: ${theme.getValue('iconPaddingLeft')};

		&::-webkit-calendar-picker-indicator {
			background: transparent;
			bottom: 0;
			color: transparent;
			cursor: pointer;
			height: auto;
			left: 0;
			position: absolute;
			right: 0;
			top: 0;
			width: auto;
		}
	`,
	icon: styled(Icon)`
		position: absolute;
		top: 50%;
		left: ${commonTheme.getValue('basePaddingHorizontal')};
		transform: translateY(-50%);
		color: ${theme.getValue('iconColor')};
		width: ${theme.getValue('iconSize')};
		height: ${theme.getValue('iconSize')};
	`,
};

export const CellulaInputDateRoot = forwardRef(function CellulaInputDateRoot(
	{
		id,
		name,
		label,
		hideLabel,
		required,
		invalid,
		invalidText,
		helperText,
		defaultValue,
		readOnly,
		onChange,
		onBlur,
		onClick,
		description,
		optional,
		...rest
	}: InputDateRootProps,
	ref: ForwardedRef<HTMLInputElement>,
): JSX.Element {
	const {
		props: inputProps,
		labelProps,
		descriptionProps,
		helpTextProps,
	} = useInput(
		{
			id,
			name,
			label,
			hideLabel,
			required,
			invalid,
			invalidText,
			helperText,
			defaultValue,
			readOnly,
			onChange,
			onBlur,
			description,
			optional,
		},
		ref,
	);
	const inputRef = useRef<HTMLInputElement>(null);
	const combinedRefs = useCombinedRefs(inputProps.ref, inputRef);

	function handleClick(event: MouseEvent<HTMLInputElement>): void {
		if (inputRef.current) {
			inputRef.current.showPicker();
		}

		onClick?.(event);
	}

	return (
		<elements.container>
			{/** biome-ignore lint/a11y/noLabelWithoutControl: suppressed due to migration - should be fixed */}
			<elements.label {...labelProps} />
			<elements.description {...descriptionProps} />

			<inputElements.container>
				<elements.icon type='calendar' />
				<elements.input {...rest} {...inputProps} ref={combinedRefs} onClick={handleClick} type='date' />
			</inputElements.container>

			<HelpText {...helpTextProps} />
		</elements.container>
	);
});
